import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Crown, Package, CheckCircle2, Truck } from "lucide-react";
import { toast } from "sonner";

export default function PinClaim() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "USA",
  });

  const load = useCallback(async () => {
    if (!user?.is_founding_member) {
      setData(null);
      return;
    }
    try {
      const r = await api.get("/tour-pass/my-pin");
      setData(r.data);
    } catch (err) {
      console.warn("pin-claim: my-pin fetch failed", err?.response?.status || err?.message);
    }
  }, [user?.is_founding_member]);

  useEffect(() => {
    load();
  }, [load, user?.id]);

  if (!user?.is_founding_member) return null;

  const claim = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/tour-pass/claim-pin", form);
      toast("Pin claimed. Ships from the next stop.");
      setOpen(false);
      setForm({
        full_name: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "USA",
      });
      await load();
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Could not claim. Try again.";
      toast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const shipment = data?.shipment;
  const isClaimed = !!shipment;
  const isShipped = shipment?.status === "shipped";

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-8 py-12" data-testid="pin-claim-section">
      <div className="border border-[#FFC107]/40 bg-[#141414] p-7 md:p-10 grid md:grid-cols-12 gap-8 items-center relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#FFC107] opacity-10 blur-3xl rounded-full" />
        <div className="md:col-span-7 relative z-10">
          <div className="overline flex items-center gap-2 text-[#FFC107]">
            <Crown size={12} fill="#FFC107" /> Founding Member · #
            {String(user.tour_pass_member_number).padStart(3, "0")}
          </div>
          <h2 className="font-display text-4xl md:text-5xl uppercase mt-3 drip-shadow">
            Your enamel pin is ready.
          </h2>
          <p className="text-white/70 mt-4 max-w-lg text-sm">
            Hand-numbered, gold-plated, hot-pink fill — your pin is hand-stamped with{" "}
            <span className="text-[#FFC107]">
              #{String(user.tour_pass_member_number).padStart(3, "0")}
            </span>{" "}
            and ships from the next city we roll into. Free for the first 100. Drop your address.
          </p>

          {isClaimed ? (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm" data-testid="pin-shipment-status">
              {isShipped ? (
                <>
                  <Truck size={16} className="text-[#22C55E]" />
                  <span className="font-display tracking-[0.18em]">Shipped</span>
                  {shipment.tracking_number && (
                    <span className="font-mono-tactical text-xs text-white/60 ml-2">
                      · {shipment.tracking_number}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} className="text-[#FFC107]" />
                  <span className="font-display tracking-[0.18em]">Claimed · ships from next stop</span>
                </>
              )}
            </div>
          ) : (
            <button
              className="btn-blaze mt-7"
              onClick={() => setOpen(true)}
              data-testid="claim-pin-button"
            >
              <Package size={14} /> Claim my pin
            </button>
          )}
        </div>
        <div className="md:col-span-5 relative z-10">
          <div className="aspect-square max-w-[280px] mx-auto relative">
            {/* Stylized pin medallion */}
            <div className="absolute inset-0 rounded-full border-[6px] border-[#FFC107] bg-[#0A0A0A] flex items-center justify-center shadow-[0_0_60px_-10px_rgba(255,200,61,0.55)]">
              <div className="text-center">
                <Crown size={28} className="mx-auto text-[#FFC107]" />
                <div className="font-display text-xl tracking-[0.2em] mt-2 text-[#FFC107]">FMAA</div>
                <div className="font-display text-5xl mt-1 text-[#FF1493] drip-shadow">
                  #{String(user.tour_pass_member_number).padStart(3, "0")}
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mt-2 font-mono-tactical">
                  Founding · 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-lg" data-testid="claim-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase tracking-wide">
              Ship my #{String(user.tour_pass_member_number).padStart(3, "0")} pin
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Hand-numbered enamel pin, ships free from the next stop. Address only used for shipping.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={claim} className="space-y-3 mt-2">
            <input
              required
              className="tac-input"
              placeholder="Full name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="pin-full-name"
            />
            <input
              required
              className="tac-input"
              placeholder="Address line 1"
              value={form.address_line1}
              onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              data-testid="pin-address1"
            />
            <input
              className="tac-input"
              placeholder="Address line 2 (optional)"
              value={form.address_line2}
              onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
              data-testid="pin-address2"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                required
                className="tac-input"
                placeholder="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="pin-city"
              />
              <input
                required
                className="tac-input"
                placeholder="State / Region"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                data-testid="pin-state"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                required
                className="tac-input"
                placeholder="Postal code"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                data-testid="pin-postal"
              />
              <input
                required
                className="tac-input"
                placeholder="Country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="pin-country"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-blaze w-full justify-center mt-2"
              data-testid="pin-claim-submit"
            >
              {submitting ? "Sending..." : "Confirm shipping address"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
